<template>
  <DSForm
    class="boxed-container boxed-container--vertical"
    scope="{ formData }"
    :prefill-form-data="prefillFormData"
    @submit="$emit('submit', formData)"
  >
    <fieldset>
      <DSSelect
        v-model="formData.award_id"
        :value="formData.award_id"
        :options="awardOptions"
        :has-groups="true"
        label="Nominee Award Category"
        class="left"
      />

      <DSSelect
        v-if="hasSizes"
        v-model="formData.organization_size_name"
        :use-name-as-value="true"
        :value="formData.organization_size_name"
        :options="orgSizeOptions"
        label="Select Organization Size"
      />
    </fieldset>
    <hr>
    <template v-if="nomineeType">
      <template v-if="nomineeType === 'Person'">
        <fieldset>
          <DSInput
            v-model="formData.nominee_first_name"
            :value="formData.nominee_first_name"
            label="Nominee's First Name (What is the name of the person you are nominating)"
            class="left"
          />
          <DSInput
            v-model="formData.nominee_last_name"
            :value="formData.nominee_last_name"
            label="Nominee's Last Name"
          />
          <DSInput
            v-model="formData.nominee_email"
            :value="formData.nominee_email"
            label="Nominee's Email"
            type="email"
            rules="email"
            class="left"
          />
          <DSInput
            v-model="formData.url"
            :value="formData.url"
            label="Nominee's Website"
            :required="false"
            rules=""
          />
          <DSInput
            v-model="formData.nominee_organization"
            :value="formData.nominee_organization"
            label="Nominee's Organization"
            :required="false"
            rules=""
            class="full-width"
          />
        </fieldset>

        <hr>

        <fieldset>
          <DSInput
            v-model="formData.nominee_address"
            :value="formData.nominee_address"
            label="Nominee's Address"
            class="full-width"
          />
          <DSInput
            v-model="formData.nominee_city"
            :value="formData.nominee_city"
            label="Nominee's City"
            class="left"
          />
          <!--TODO: Make label dynamic based on country selection-->
          <DSRegionSelect
            v-model="formData.nominee_state"
            :country="formData.nominee_country"
            label="Nominee's State/Province/Region"
          />
          <DSInput
            v-model="formData.nominee_zip_code"
            :value="formData.nominee_zip_code"
            label="Nominee's Zip Code"
            class="left"
          />
          <DSCountrySelect
            v-model="formData.nominee_country"
            label="Nominee's Country/Region"
          />
        </fieldset>

        <hr>
      </template>
      <template v-else-if="nomineeType === 'Organization'">
        <fieldset>
          <DSInput
            v-model="formData.nominee_organization"
            :value="formData.nominee_organization"
            label="Nominee Organization Name (What is the name of the organization you are nominating)"
            :required="true"
            rules=""
            class="full-width"
          />
        </fieldset>

        <hr>
        <fieldset>
          <DSInput
            v-model="formData.nominee_address"
            :value="formData.nominee_address"
            label="Nominee Organization Address"
            class="full-width"
          />
          <DSInput
            v-model="formData.nominee_city"
            :value="formData.nominee_city"
            label="Nominee Organization City"
            class="left"
          />
          <!--TODO: Make label dynamic based on country selection-->
          <DSRegionSelect
            v-model="formData.nominee_state"
            :country="formData.nominee_country"
            label="Nominee Organization State/Province/Region"
          />
          <DSInput
            v-model="formData.nominee_zip_code"
            :value="formData.nominee_zip_code"
            label="Nominee Organization Zip Code"
            class="left"
          />
          <DSCountrySelect
            v-model="formData.nominee_country"
            label="Nominee Organization Country/Region"
          />

          <DSInput
            v-model="formData.url"
            :value="formData.url"
            label="Nominee Organization Website"
            :required="true"
            class="left"
            rules=""
          />
        </fieldset>

        <hr>

        <fieldset>
          <DSInput
            v-model="formData.nominee_first_name"
            :value="formData.nominee_first_name"
            label="Nominee Organization Contact Person First Name"
            class="left"
          />
          <DSInput
            v-model="formData.nominee_last_name"
            :value="formData.nominee_last_name"
            label="Nominee Organization Contact Person Last Name"
          />
          <DSInput
            v-model="formData.nominee_email"
            :value="formData.nominee_email"
            label="Nominee Organization Contact Person Email"
            type="email"
            rules="email"
            class="left"
          />
        </fieldset>
        <hr>
      </template>

      <fieldset>
        <p v-if="nomineeType === 'Person'">
          Additional grants may be awarded to the winner’s associated non-profit organization. Please complete the next two fields.
        </p>
        <p v-else-if="nomineeType === 'Organization'">
          Organizations do not need to be a non-profit to win a Purple Ribbon Award. Additional grants may be awarded to the winner’s associated non-profit organization. If your organization is not a non-profit, please complete the next two fields.
        </p>
        <DSInput
          v-model="formData.non_profit_name"
          :value="formData.non_profit_name"
          label="Associated Nonprofit Organization Name"
          :required="false"
          rules=""
          class="left"
        />
        <DSInput
          v-model="formData.non_profit_website"
          :value="formData.non_profit_website"
          :required="false"
          rules=""
          label="Associated Nonprofit Organization Website"
        />
      </fieldset>

      <hr>

      <fieldset>
        <DSTextArea
          v-model="formData.description"
          :value="formData.description"
          rules="required|maxWords:300"
          label="Description of Nominee"
          placeholder="Use this field to briefly share how your nominee has overcome challenges, made an impact and done so with creative and original ideas..."
          additional-info="Each entry will be judged on four criteria: <strong>Challenge &amp; Impact</strong> (the scope of the challenge faced and overcome, and the entry outcomes), <strong>Creativity &amp; Originality</strong> (the inventiveness of the nominee in their endeavor), <strong>Submission Excellence</strong> (the caliber of the materials accompanying the entry) and <strong>Overall</strong> (the overall performance of the entry relative to other entries).<br><br>Please address the above criteria in your description (300 word limit)."
        />
      </fieldset>

      <hr>

      <h3>Narrative Questions</h3>
      <fieldset>
        <DSTextArea
          v-model="formData.outcomes"
          :value="formData.outcomes"
          label="Outcomes"
          additional-info="Describe how this individual, team, or organization has made a difference in the domestic violence field personally, locally, nationally, or internationally. What were some challenges that were addressed? If available, please share data. (300 word limit)"
          rules="required|maxWords:300"
        />
        <DSTextArea
          v-model="formData.hope_story"
          :value="formData.hope_story"
          label="Hope Story"
          additional-info="Share a story that demonstrates the impact/work of the nominee. (150 word limit)"
          rules="required|maxWords:150"
        />
        <DSTextArea
          v-model="formData.media_links"
          :value="formData.media_links"
          label="Media Links"
          :required="false"
          rules="maxLines:5"
          additional-info="Add one link per line to relevant media such as videos, press coverage, etc. that will assist the judges in evaluating your entry. (Limit 5)"
        />
      </fieldset>

      <div v-if="editMode">
        <hr>
        <h3>Previously Attached Files</h3>
        <NominationFiles
          :files="uploadedFiles"
          @remove="removeFile"
        />
      </div>

      <hr>

      <fieldset>
        <DSFileUpload
          ref="fileUpload"
          :button-labels="fileUploadLabels"
          :previously-uploaded-files="uploadedFiles"
          @upload-files="uploadMore"
        >
          <template v-slot:default="slotProps">
            <p>
              Please include at least one and up to five attachments that support the narrative included in the nomination. Files will be used to assist the judges in evaluating your entry.
            </p>
            <p>
              Attach materials that support this nomination such as:
            </p>
            <ul>
              <li>Annual Reports</li>
              <li>Brochures/Flyers</li>
              <li>Organization Logo</li>
              <li>Photos & Headshots</li>
              <li>Success Stories/Case Studies</li>
              <li>Testimonials/Letters of Support</li>
            </ul>
            <p>
              <em>{{ slotProps.filesTypeNote }}</em>
            </p>
            <p>
              <strong>You are <em>not</em> required to upload <em>all</em> these types of files.</strong>
            </p>
          </template>
        </DSFileUpload>
      </fieldset>

      <fieldset>
        <DSButton
          color="primary"
          type="submit"
          :invalid="invalid"
        >
          Save Nomination &
          Upload Files
        </DSButton>
      </fieldset>
    </template>
  </DSForm>
</template>

<script>
import { getAwardTitles, getOrganizationSizes, getAwardNoOrganizationSizes, getParsedAwardData, removeFile, getAwards } from '../services/HttpSvc'
import * as DSFormComponents from './forms'
import NominationFiles from './NominationFiles'
import { USER_NOMINATIONS } from '../core/constants'
import DSTextArea from "./forms/DSTextArea";
export default {
  name: 'NominateForm',

  components: {
    DSTextArea,
    ...DSFormComponents,
    NominationFiles,
  },

  props: {
    submitLabel: {
      type: String,
      default: '',
    },
    nominationId: {
      type: Number,
      default: () => -1,
    },
    prefillFormData: {
      type: Object,
      default: () => {
        return {
          nominee_address: '',
          nominee_first_name: '',
          nominee_last_name: '',
          nominee_city: '',
          nominee_country: '',
          nominee_state: '',
          nominee_zip_code: '',
          nominee_email: '',
          nominee_organization: '',
          organization_size_id: '',
          organization_size_name: '',
          award_id: '',
          organization_name: '',
          title: '',
          description: '',
          url: '',
          non_profit_name: '',
          non_profit_website: '',
          media_links: '',
          outcomes: '',
          hope_story: '',
        }
      },
    },
    uploadedFiles: {
      type: Array,
      default: () => [],
    },
    invalid: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      awardData: {},
      awards: [],
      formData: {},
      awardOptions: {},
      orgSizeOptions: {},
      awardNoOrganizationSizes: {},
      fileUploadLabels: [
        'files',
      ],
    }
  },

  computed: {

    editMode() {
      return this.uploadedFiles.length > 0
    },

    selectedAward(){
      const award_id = this.formData.award_id;
      if (typeof award_id === 'undefined' || award_id === ''){
        return null
      }

      return this.awards.find(a => a.id.toString() === award_id);
    },

    nomineeType() {
      if (!this.selectedAward){
        return null
      }

      if (this.selectedAward.is_organization){
        return 'Organization';
      }
      return 'Person';
    },

    hasSizes(){
      if(this.selectedAward) {
        return !this.selectedAward.no_organization_size;
      }
      return null;
    },
  },

  watch: {
    prefillFormData: {
      immediate: true,
      handler(formData) {
        this.setData(formData)
      },
    },
  },

  async created() {
    const [awardData, organizationSizes] = await Promise.all([getAwards(), getOrganizationSizes()]);
    this.awardData = awardData;
    this.awards = getParsedAwardData(awardData);
    const dropdownRequests = [getAwardTitles(awardData), organizationSizes, getAwardNoOrganizationSizes(awardData)];
    if (this.prefillFormData) {
      this.setData(this.prefillFormData)
    }
    [this.awardOptions, this.orgSizeOptions, this.awardNoOrganizationSizes] = dropdownRequests
  },

  methods: {
    setData(formData) {
      if (formData.organization_size_name) {
        formData.organization_size_name = formData.organization_size_name.toString()
      }
      if(formData.award_id){
        formData.award_id = formData.award_id.toString()
      }
      this.formData = formData
    },
    cancel() {
      window.location = USER_NOMINATIONS
    },
    uploadFiles(nominationId = this.nominationId) {
      return this.$refs.fileUpload.uploadFiles(nominationId)
    },
    async uploadMore() {
      await this.$refs.fileUpload.uploadFiles(this.nominationId)
      this.setData(this.prefillFormData)
      this.$emit('files-changed')
    },
    removeFile(file) {
      removeFile(file.id)
      this.uploadedFiles.splice(this.uploadedFiles.indexOf(file), 1)
    },
  },

}
</script>
